<template>
    <div class="app">
        <!-- 全局粒子背景：铺在所有内容之下 -->
        <ParticleField />

        <!-- 登录等公开页：不套外壳，整页自由布局 -->
        <router-view v-if="isPublic" />

        <!-- 业务页：左侧导航 + 顶部状态栏 的控制台外壳 -->
        <div v-else class="shell">
            <!-- 移动端抽屉打开时的遮罩 -->
            <div v-if="drawer" class="scrim" @click="drawer = false"></div>

            <aside class="sidebar" :class="{ open: drawer }">
                <div class="brand">
                    <div class="brand-orb"></div>
                    <div class="brand-text">
                        <div class="brand-title flow-text">A-UNICORN</div>
                        <div class="brand-sub muted2">KEY SYSTEM CONSOLE</div>
                    </div>
                </div>

                <nav class="nav">
                    <RouterLink v-for="item in nav" :key="item.path" :to="item.path" class="nav-item"
                        active-class="on" @click="drawer = false">
                        <span class="nav-ico">{{ item.icon }}</span>
                        <span class="nav-label">{{ item.label }}</span>
                        <span class="nav-glow"></span>
                    </RouterLink>
                </nav>

                <div class="side-foot">
                    <div class="net-card">
                        <span class="dot live"></span>
                        <div class="net-text">
                            <div class="net-title">服务在线</div>
                            <div class="muted2 net-host">www.czymf-tool.cn</div>
                        </div>
                    </div>
                    <button class="btn btn-danger logout" @click="logout">退出登录</button>
                </div>
            </aside>

            <div class="main">
                <header class="topbar card scanline">
                    <button class="menu-btn" @click="drawer = true" aria-label="菜单">☰</button>

                    <div class="topbar-title">
                        <span class="t-ico">{{ currentIcon }}</span>
                        <div>
                            <div class="t-name">{{ currentTitle }}</div>
                            <div class="t-path muted2">/admin{{ route.path }}</div>
                        </div>
                    </div>

                    <div class="topbar-right">
                        <span class="badge clock mono">{{ clock }}</span>
                        <span class="badge online-badge">
                            <span class="dot live"></span> ONLINE
                        </span>
                    </div>
                </header>

                <main class="content">
                    <router-view v-slot="{ Component }">
                        <transition name="page" mode="out-in">
                            <component :is="Component" />
                        </transition>
                    </router-view>
                </main>
            </div>
        </div>

        <!-- 全局 Toast：通过 this.$root.$toast 调用 -->
        <ToastHost ref="toast" />
    </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "./stores/auth";

import ParticleField from "./components/ParticleField.vue";
import ToastHost from "./components/ToastHost.vue";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

// 侧边导航项
const nav = [
    { path: "/keys", label: "卡密管理", icon: "🔑" },
    { path: "/online", label: "在线监控", icon: "📡" },
    { path: "/events", label: "异常事件", icon: "⚠" },
    { path: "/builds", label: "版本管理", icon: "🧬" },
];

const drawer = ref(false);
const isPublic = computed(() => !!route.meta.public);
const currentTitle = computed(() => route.meta.title || "控制台");
const currentIcon = computed(() => route.meta.icon || "✦");

// 顶部小时钟，纯展示用，强化"实时面板"观感
const clock = ref("");
let clockTimer = null;
function tick() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    clock.value = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function logout() {
    auth.logout();
    router.push("/login");
}

// 把 Toast 实例挂到 $root，沿用旧的全局调用方式
const toast = ref(null);
const inst = getCurrentInstance();

onMounted(() => {
    if (inst) inst.root.proxy.$toast = toast.value;
    tick();
    clockTimer = setInterval(tick, 1000);
});

onBeforeUnmount(() => {
    if (clockTimer) clearInterval(clockTimer);
});
</script>

<style>
/* 这里不加 scoped：外壳布局变量需要被子页面共享，但样式选择器都带前缀避免污染 */
.app {
    min-height: 100vh;
}

.shell {
    display: grid;
    grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
    min-height: 100vh;
}

/* ---------------- 侧边导航 ---------------- */
.sidebar {
    position: sticky;
    top: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 20px 16px;
    background: linear-gradient(180deg, rgba(255, 255, 255, .05), rgba(255, 255, 255, .015));
    border-right: 1px solid var(--stroke);
    backdrop-filter: blur(16px);
    z-index: 30;
}

.brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 6px;
}

.brand-orb {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    flex: 0 0 auto;
    background: var(--grad-main);
    box-shadow: var(--glow-violet);
    position: relative;
    animation: spin-slow 9s linear infinite;
}

/* orb 外圈呼吸光晕：让品牌标识更有"能量核心"感 */
.brand-orb::before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 16px;
    background: var(--grad-main);
    filter: blur(10px);
    opacity: .5;
    z-index: -1;
    animation: orb-breathe 3.5s ease-in-out infinite;
}

@keyframes orb-breathe {

    0%,
    100% {
        opacity: .35;
        transform: scale(.92);
    }

    50% {
        opacity: .7;
        transform: scale(1.08);
    }
}

.brand-orb::after {
    content: "";
    position: absolute;
    inset: 5px;
    border-radius: 8px;
    background: var(--bg-1);
}

.brand-title {
    font-weight: 900;
    font-size: 17px;
    letter-spacing: .08em;
}

.brand-sub {
    font-size: 10px;
    letter-spacing: .14em;
    margin-top: 2px;
}

.nav {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
}

.nav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: var(--r-sm);
    border: 1px solid transparent;
    color: var(--muted);
    cursor: pointer;
    overflow: hidden;
    transition: color var(--t), border-color var(--t), background var(--t), transform var(--t-fast);
}

.nav-item:hover {
    color: var(--text);
    background: rgba(255, 255, 255, .04);
    transform: translateX(4px);
}

/* hover 时一束斜光扫过导航项，呼应整体"通电"质感 */
.nav-item::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(0, 229, 255, .14) 50%, transparent 70%);
    transform: translateX(-120%) skewX(-16deg);
    transition: transform .6s cubic-bezier(.22, 1, .36, 1);
    pointer-events: none;
}

.nav-item:hover::after {
    transform: translateX(120%) skewX(-16deg);
}

.nav-item.on {
    color: #fff;
    border-color: rgba(0, 229, 255, .4);
    background: linear-gradient(90deg, rgba(124, 92, 255, .22), rgba(0, 229, 255, .08));
    box-shadow: var(--glow-cyan);
}

/* 选中项左侧的发光指示条 */
.nav-item.on::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 999px;
    background: var(--grad-main);
}

.nav-ico {
    font-size: 18px;
    width: 22px;
    text-align: center;
    transition: transform var(--t) cubic-bezier(.22, 1, .36, 1), filter var(--t);
}

.nav-item:hover .nav-ico {
    transform: scale(1.22) rotate(-6deg);
}

.nav-item.on .nav-ico {
    filter: drop-shadow(0 0 8px rgba(0, 229, 255, .9));
    animation: nav-ico-pulse 2.4s ease-in-out infinite;
}

@keyframes nav-ico-pulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.12);
    }
}

.nav-label {
    font-size: 14px;
    font-weight: 600;
}

.side-foot {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.net-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--r-sm);
    border: 1px solid var(--stroke);
    background: rgba(0, 0, 0, .2);
}

.net-title {
    font-size: 13px;
    font-weight: 600;
}

.net-host {
    font-size: 11px;
}

.logout {
    width: 100%;
}

/* ---------------- 主区域 ---------------- */
.main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 100vh;
}

.topbar {
    position: sticky;
    top: 12px;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 12px 16px 0;
    padding: 10px 16px;
    border-radius: var(--r-md);
}

.menu-btn {
    display: none;
    border: 1px solid var(--stroke);
    background: rgba(255, 255, 255, .05);
    color: var(--text);
    width: 40px;
    height: 40px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 18px;
}

.topbar-title {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
}

.t-ico {
    font-size: 22px;
    filter: drop-shadow(0 0 10px rgba(0, 229, 255, .5));
    animation: hover-float 5s ease-in-out infinite;
}

.t-name {
    font-size: 17px;
    font-weight: 800;
}

.t-path {
    font-size: 11px;
    font-family: "JetBrains Mono", monospace;
}

.topbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.online-badge {
    color: #b6ffe5;
}

.content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 16px;
}

/* 移动端抽屉遮罩 */
.scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .55);
    backdrop-filter: blur(2px);
    z-index: 25;
}

/* 路由切换过渡：3D 翻入 + 模糊，营造"页面从纵深翻出来"的感觉 */
.page-enter-active {
    transition: opacity .42s cubic-bezier(.16, 1, .3, 1), transform .42s cubic-bezier(.16, 1, .3, 1), filter .42s;
}

.page-leave-active {
    transition: opacity .26s ease, transform .26s ease, filter .26s;
}

.page-enter-from {
    opacity: 0;
    transform: perspective(1200px) rotateX(8deg) translateY(26px) scale(.985);
    filter: blur(8px);
}

.page-leave-to {
    opacity: 0;
    transform: perspective(1200px) rotateX(-4deg) translateY(-14px) scale(.99);
    filter: blur(4px);
}

@keyframes spin-slow {
    to {
        transform: rotate(360deg);
    }
}

/* ---------------- 响应式：≤1024 收起为抽屉 ---------------- */
@media (max-width: 1024px) {
    .shell {
        grid-template-columns: 1fr;
    }

    .sidebar {
        position: fixed;
        left: 0;
        top: 0;
        width: 260px;
        transform: translateX(-105%);
        transition: transform var(--t) cubic-bezier(.22, 1, .36, 1);
    }

    .sidebar.open {
        transform: translateX(0);
        box-shadow: var(--shadow);
    }

    .menu-btn {
        display: grid;
        place-items: center;
    }
}

@media (max-width: 768px) {

    /* 手机端顶栏精简：时钟、路径、ONLINE 徽章都收掉，
       只保留菜单按钮 + 当前页标题，避免互相重叠挤压 */
    .clock,
    .t-path,
    .online-badge {
        display: none;
    }

    .topbar {
        margin: 12px 12px 0;
        gap: 10px;
        padding: 8px 12px;
    }

    .t-ico {
        font-size: 19px;
    }

    .t-name {
        font-size: 15px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .content {
        padding: 12px;
    }
}
</style>
